import { clamp, set } from 'lodash-es';
import type { OperatorFunction } from 'rxjs';
import { Observable, Subject, mergeMap, retry, throwError } from 'rxjs';
import type { Camera, Raycaster, Scene, Vector3Tuple } from 'three';

import { getPlanePoint } from '../three';

export const _createSubscriber = <E, R>(
  $ob: Observable<E>,
  handler: (value: E) => R,
) => {
  const subject = new Subject<R>();

  $ob.subscribe({
    next(value) {
      subject.next(handler(value));
    },
  });

  return subject;
};

export const filterOrRestart = <T>(
  predicate: (v: T) => boolean,
): OperatorFunction<T, T> => {
  return ob$ =>
    ob$.pipe(
      mergeMap(v => (predicate(v) ? [v] : throwError(() => new Error()))),
      retry(),
    );
};

const _genAssign =
  <K extends string, V>(key: K) =>
  <E extends {}>(val: V) =>
  ($ob: Observable<E>) =>
    _createSubscriber($ob, e => setProperty(e, key, val));

const setProperty = <
  T extends {},
  K extends string,
  V,
  R extends T & { readonly [key in K]: V },
>(
  obj: T,
  key: K,
  value: V,
): R => set(obj, key, value) as unknown as R;

const setProperties = <
  T extends {},
  E extends {},
  R extends T & {
    readonly [K in keyof E]: E[K];
  },
>(
  obj: T,
  keyVal: E,
): R => {
  Object.entries(keyVal).forEach(([key, val]) => {
    setProperty(obj, key, val);
  });

  return obj as unknown as R;
};

export const withTarget = <
  E extends Event,
  R extends With<Omit<E, 'target'>, { target: HTMLElement }>,
>(
  $ob: Observable<E>,
) => _createSubscriber($ob, value => value as unknown as R);

export const withCanvasXY = <E extends MouseEventWith<{ target: HTMLElement }>>(
  $ob: Observable<E>,
) =>
  _createSubscriber($ob, value => {
    const { clientX, clientY, target } = value;
    const { offsetWidth, offsetHeight } = target;
    const { x, y } = target.getBoundingClientRect();
    const clampedClientX = clamp(clientX, x, x + offsetWidth);
    const clampedClientY = clamp(clientY, y, y + offsetHeight);
    const [canvasX, canvasY] = [
      ((clampedClientX - x) / offsetWidth) * 2 - 1,
      -((clampedClientY - y) / offsetHeight) * 2 + 1,
    ];

    return setProperties(value, {
      canvasX,
      canvasY,
    });
  });

export const assignThreeRaycaster = _genAssign<'raycaster', Raycaster>(
  'raycaster',
);

export const assignThreeScene = _genAssign<'scene', Scene>('scene');

export const assignThreeCamera = _genAssign<'camera', Camera>('camera');

export const withVector3 =
  <
    E extends MouseEventWith<{
      canvasX: number;
      canvasY: number;
      raycaster: Raycaster;
      camera: Camera;
    }>,
  >(
    $ob: Observable<E>,
  ) =>
  (vector: Vector3Tuple = [0, 0, 1]) =>
    _createSubscriber($ob, value => {
      const point = getPlanePoint({
        x: value.canvasX,
        y: value.canvasY,
        raycaster: value.raycaster,
        camera: value.camera,
        vector,
      });

      return setProperty(value, 'point', point);
    });

export const createIntersectionObserver = (
  element: Element,
  options?: IntersectionObserverInit,
): Observable<boolean> => {
  return new Observable<boolean>(subscriber => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => subscriber.next(entry.isIntersecting));
    }, options);

    observer.observe(element);

    return () => observer.disconnect();
  });
};
